// SEO-friendly public shop URL. Resolves a seller by username (preferred) or
// UUID, reuses the existing seller-profile data via `public_sellers`, and
// renders a badge-aware header before the seller's active auctions.
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { resolvePublicSellerName } from "@/lib/public-name";
import { fetchOfficialPartnerProfileIds } from "@/lib/advertising";
import { OfficialPartnerBadge } from "@/components/official-partner-badge";
import { VerifiedSellerBadge } from "@/components/verified-seller-badge";
import { AuctionGrid } from "@/components/auction-grid";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Star } from "lucide-react";
import type { AuctionCardData } from "@/components/auction-card";
import { useTranslation } from "@/i18n";

type ShopSeo = {
  id: string;
  username: string | null;
  display_name: string | null;
  shop_name: string | null;
  bio: string | null;
  avatar_url: string | null;
  seller_profile_enabled: boolean;
  seller_rating: number | null;
  seller_rating_count: number | null;
  seller_sales_count: number | null;
} | null;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute("/shops/$slug")({
  loader: async ({ params }): Promise<{ shop: ShopSeo }> => {
    try {
      const handle = params.slug;
      const isUuid = UUID_RE.test(handle);
      const q = supabase
        .from("public_sellers" as never)
        .select(
          "id,username,display_name,shop_name,bio,avatar_url,seller_profile_enabled,seller_rating,seller_rating_count,seller_sales_count",
        );
      const { data } = isUuid
        ? await q.eq("id", handle).maybeSingle()
        : await q.ilike("username", handle.trim().toLowerCase()).maybeSingle();
      return { shop: (data as ShopSeo) ?? null };
    } catch {
      return { shop: null };
    }
  },
  head: ({ params, loaderData }) => {
    const s = loaderData?.shop;
    const handle = s?.username ?? params.slug;
    const url = `https://bidnovaauctions.uk/shops/${handle}`;
    const name = resolvePublicSellerName(s ?? undefined);
    const enabled = s?.seller_profile_enabled ?? false;
    const title = `${name} Shop | BidNova Auctions`;
    const desc = (s?.bio?.trim() ||
      `Browse live auctions, trading cards, accessories and collectibles from ${name} on BidNova Auctions.`)
      .replace(/\s+/g, " ")
      .slice(0, 155);
    const image = s?.avatar_url ?? undefined;
    const meta: Array<Record<string, string>> = [
      { title },
      { name: "description", content: desc },
      { property: "og:title", content: title },
      { property: "og:description", content: desc },
      { property: "og:url", content: url },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: desc },
    ];
    if (image) {
      meta.push({ property: "og:image", content: image }, { name: "twitter:image", content: image });
    }
    if (!enabled || !s) meta.push({ name: "robots", content: "noindex,follow" });
    return {
      meta,
      links: [{ rel: "canonical", href: url }],
      scripts:
        enabled && s
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "Store",
                  name,
                  url,
                  image: image ?? undefined,
                  description: desc,
                }),
              },
            ]
          : undefined,
    };
  },
  errorComponent: () => <ShopErrorFallback />,
  notFoundComponent: () => <ShopNotFoundFallback />,
  component: ShopPage,
});

function ShopErrorFallback() {
  const { t } = useTranslation("community");
  return <div className="p-16 text-center text-muted-foreground">{t("shops.detail.unavailable", "Shop unavailable.")}</div>;
}

function ShopNotFoundFallback() {
  const { t } = useTranslation("community");
  return <div className="p-16 text-center text-muted-foreground">{t("shops.detail.notFound", "Shop not found.")}</div>;
}

function ShopPage() {
  const { t } = useTranslation("community");
  const { shop } = Route.useLoaderData();
  if (!shop) throw notFound();

  const name = resolvePublicSellerName(shop);
  const rating = shop.seller_rating ? Number(shop.seller_rating) : 0;
  const ratingCount = shop.seller_rating_count ?? 0;
  const hasRating = ratingCount > 0 && rating > 0;

  const { data: officialSet } = useQuery({
    queryKey: ["official-partner-profile-ids"],
    queryFn: fetchOfficialPartnerProfileIds,
    staleTime: 5 * 60_000,
  });
  const isOfficial = !!officialSet?.has(shop.id);

  const { data: auctions } = useQuery({
    queryKey: ["shop-active-auctions", shop.id],
    queryFn: async (): Promise<AuctionCardData[]> => {
      const { data } = await supabase
        .from("auctions")
        .select("*")
        .eq("seller_id", shop.id)
        .eq("status", "active")
        .order("end_time", { ascending: true })
        .limit(24);
      return (data ?? []) as unknown as AuctionCardData[];
    },
  });

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
      <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-purple-950/30 via-black to-black p-6 sm:p-8">
        <div className="flex flex-col items-start gap-6 sm:flex-row">
          <Avatar className="h-24 w-24 border border-purple-400/40">
            <AvatarImage src={shop.avatar_url ?? undefined} alt={`${name} logo`} />
            <AvatarFallback className="bg-purple-500/15 text-purple-100 font-display text-2xl">
              {name.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-display text-3xl text-white sm:text-4xl">{name}</h1>
              <VerifiedSellerBadge />
              {isOfficial && <OfficialPartnerBadge />}
            </div>
            {shop.bio && <p className="mt-3 max-w-2xl text-muted-foreground">{shop.bio}</p>}
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              {hasRating && (
                <span className="inline-flex items-center gap-1.5">
                  <Star className="h-4 w-4 fill-gold text-gold" />
                  <span className="text-white">{rating.toFixed(1)}</span>
                  <span>({ratingCount})</span>
                </span>
              )}
              <span>{t("shops.detail.sales", "{count, plural, one {# sale} other {# sales}}", { count: shop.seller_sales_count ?? 0 })}</span>
            </div>
          </div>
          <Button asChild variant="outline" className="border-purple-400/40 text-purple-200 hover:bg-purple-500/10">
            <Link to="/seller/$sellerId" params={{ sellerId: shop.username ?? shop.id }}>
              {t("shops.detail.fullProfile", "Full profile")}
            </Link>
          </Button>
        </div>
      </div>

      <section className="mt-10">
        <h2 className="mb-4 font-display text-2xl text-white">{t("shops.detail.liveAuctions", "Live auctions")}</h2>
        {(auctions ?? []).length === 0 ? (
          <p className="text-muted-foreground">{t("shops.detail.noAuctions", "No live auctions from this shop right now.")}</p>
        ) : (
          <AuctionGrid auctions={auctions ?? []} />
        )}
      </section>
    </div>
  );
}
